import Box from '@/Atoms/Box';
import {FontSizes, hs, isPhone, vs} from '@/Utils/Dimensions';

import React, {useState, useEffect, useCallback, useRef, useMemo} from 'react';
import {
  TextInput,
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  TouchableWithoutFeedback,
  FlatList,
} from 'react-native';
import {debounce} from 'lodash';

import CCTextInput from '@/Atoms/CCTextInput';
import CCColors from '@/Utils/CCColors';
import ConditionalRender from '@/Atoms/ConditionalRender';
import CCButton from '@/Atoms/CCButton';
import CCText from '@/Atoms/CCText';
import Divider from '@/Atoms/Divider/Divider';
import Select from 'react-select';
import {GestureResponderEvent} from 'react-native';
import useLoggedInUserStore from '@/Store/loggedInUserstore';
import {
  getLocalStorage,
  removeLocalStorage,
  reverseObject,
} from '@/Utils/CommonUtils';

function ChallengeUserComponent(props) {
  const {handleOpponentName, searchPlayers, searchPlayersResult, user} = props;
  const [loadingPlayer, setLoadingPlayer] = useState(false);
  const [searchTerm, setSearchTerm] = useState(null);
  const [leagueOppId, setLeagueOppId] = useState('');
  const [displayOptions, setDisplayOptions] = useState([]);
  const inputValueRef = useRef('');
  const localOptionsRef = useRef([]);
  const searchPlayersRef = useRef(searchPlayers);

  useEffect(() => {
    searchPlayersRef.current = searchPlayers;
  }, [searchPlayers]);

  const debouncedSearch = useMemo(
    () =>
      debounce(text => {
        searchPlayersRef.current(text);
      }, 300),
    [],
  );

  const filterAndSort = useCallback((options, inputValue) => {
    if (!inputValue) return options;
    const lowercaseInput = inputValue.toLowerCase();
    return options
      .filter(option => option.label.toLowerCase().includes(lowercaseInput))
      .sort((a, b) => {
        const aStartsWith = a.label.toLowerCase().startsWith(lowercaseInput);
        const bStartsWith = b.label.toLowerCase().startsWith(lowercaseInput);
        if (aStartsWith && !bStartsWith) return -1;
        if (!aStartsWith && bStartsWith) return 1;
        return a.label.toLowerCase().localeCompare(b.label.toLowerCase());
      });
  }, []);

  const handleUsernameChange = useCallback(
    username => {
      // Update the search term state
      setSearchTerm(username);
      // Call the debounced searchPlayers function
      searchPlayers(username);
    },
    [searchPlayers],
  );

  const handleFocus = () => {
    inputValueRef.current = '';
    setDisplayOptions(localOptionsRef.current);
    handleUsernameChange('');
    console.log('handleFocus run');
    setLoadingPlayer(true);
    setIsFocused(true);
  };

  useEffect(() => {
    const updateUsername = async () => {
      const league_opp_id = await getLocalStorage('league_opp_id');
      if (league_opp_id) {
        setLeagueOppId(league_opp_id);
      } else {
        setLeagueOppId('');
      }
    };
    updateUsername();
  }, []);

  const handleBlur = () => {
    // Delay setting isFocused to false by 150 milliseconds
    setTimeout(() => {
      setIsFocused(false);
    }, 300); // 150 milliseconds
  };

  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    console.log('player list = ', searchPlayersResult);
    if (searchPlayersResult) {
      setLoadingPlayer(false);
      if (leagueOppId) {
        const players = reverseObject(JSON.parse(searchPlayersResult));
        const username = players[leagueOppId];
        if (username) {
          setSearchTerm({
            value: `${leagueOppId}:${username}`,
            label: username,
          });
          handleOpponentName(`${leagueOppId}:${username}`);
        }
      } else {
        try {
          let parsedResult = JSON.parse(searchPlayersResult);
          parsedResult = Object.fromEntries(
            Object.entries(parsedResult).filter(
              ([key, value]) => value !== user,
            ),
          );
          let resultArray = Object.keys(parsedResult).map(username => ({
            username: username,
            userId: parsedResult[username],
          }));
          console.log('parsed list = ', parsedResult);
          console.log('resultArray = ', resultArray);
          const newOptions = resultArray.map(player => ({
            value: `${player.userId}:${player.username}`,
            label: player.username,
          }));
          localOptionsRef.current = newOptions;

          // Update dropdown with fresh data, filtered by current input
          setDisplayOptions(filterAndSort(newOptions, inputValueRef.current));
        } catch (error) {
          console.error('Error parsing searchPlayersResult:', error);
        }
        if (!searchTerm) {
          handleOpponentName('');
        }
      }
    }
  }, [searchPlayersResult]);

  const handlePlayerSelect = selectedPlayer => {
    // React-select passes null when user clears selection
    if (!selectedPlayer) {
      inputValueRef.current = '';
      setSearchTerm(null);
      setLeagueOppId('');
      setDisplayOptions(localOptionsRef.current);
      handleOpponentName('');
      void removeLocalStorage('league_opp_id');
      return;
    }

    // User explicitly selected a player; stop league prefill flow
    inputValueRef.current = '';
    setLeagueOppId('');
    setSearchTerm(selectedPlayer);
    handleOpponentName(selectedPlayer?.value);
    setDisplayOptions(localOptionsRef.current);
    void removeLocalStorage('league_opp_id');
  };

  const handleInputChange = useCallback(
    (newValue, {action}) => {
      if (action !== 'input-change') return newValue;

      inputValueRef.current = newValue;
      // Once user starts typing, clear stored/prefilled league opponent.
      setLeagueOppId('');

      // Filter locally and update display immediately
      setDisplayOptions(
        filterAndSort(localOptionsRef.current, newValue),
      );

      // Trigger debounced websocket search for fresh results
      setLoadingPlayer(true);
      debouncedSearch(newValue || '');
      void removeLocalStorage('league_opp_id');
      return newValue;
    },
    [debouncedSearch, filterAndSort],
  );

  const handleMenuOpen = useCallback(() => {
    setDisplayOptions(
      filterAndSort(localOptionsRef.current, inputValueRef.current || ''),
    );
  }, [filterAndSort]);

  return (
    <View style={styles.container}>
      <Box overflow="visible" style={{zIndex: -10, width: '80%'}}>
        <Select
          options={displayOptions}
          onInputChange={handleInputChange}
          filterOption={null}
          value={searchTerm}
          onChange={handlePlayerSelect}
          onMenuOpen={handleMenuOpen}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder="Username of the opponent"
          isClearable
          isLoading={loadingPlayer}
          styles={{
            menu: provided => ({
              ...provided,
              maxHeight: 150,
              overflowY: 'auto',
            }),
            menuList: provided => ({
              ...provided,
              maxHeight: 150,
              overflowY: 'auto',
            }),
          }}
        />
      </Box>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    position: 'relative',
    alignItems: 'flex-start',
    padding: 8,
    zIndex: -10,
    // width: '90%'
  },
  input: {
    backgroundColor: 'white',
    borderColor: '#22222220',
    borderWidth: 1,
    width: isPhone ? '90vw' : '24vw',
    height: 48,
    borderRadius: 8,
    padding: 12,
    placeholderTextColor: '#26232240',
    zIndex: -10,
  },
  dropdown: {
    position: 'relative',
    backgroundColor: 'white',
    borderRadius: 8,
    borderColor: '#22222220',
    borderWidth: 1,
    width: 480,
    zIndex: 0,
    bottom: 0,
  },
  autoCompleteFlatList: {
    position: 'absolute',
    width: '100%',
    top: '100%',
    left: 0,
    backgroundColor: CCColors.White,
    borderColor: CCColors.BorderBottom,
    borderRadius: 10,
  },
  divider: {
    width: '90%',
    marginLeft: 'auto',
    marginRight: 'auto',
  },
  dropdownItem: {
    padding: 12,
  },
});

export default ChallengeUserComponent;
